import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { MissionRequest } from 'src/app/shared/models/mission/mission-request';
import { ImportResponse } from 'src/app/shared/models/api-response/import-response';
import { ConstantsService } from '../constant/constants.service';
import { Mission } from 'src/app/shared/models/mission/mission';
import { GenericCrudService } from '../generic-crud.service';

@Injectable({
	providedIn: 'root'
})
export class FormationService {
	constructor(
		private appConstants: ConstantsService,
		private genericCrudService: GenericCrudService
	) {}

	/**
	 * Récupère une liste de missionRequest entre 2 Date
	 * @param startDate
	 * @param endDate
	 * @returns
	 */
	getFormations(startDate: Date, endDate: Date): Observable<MissionRequest[]> {
		return this.genericCrudService.genericGetAPIData(
			`${
				this.appConstants.formationUrl
			}s/start/${startDate.getTime()}/end/${endDate.getTime()}`,
			'getFormations'
		);
	}

	/**
	 * Renvoi la liste des missionRequest auquel participe un beneficiaire donner
	 * @param beneficiaireId id du bénéficiaire
	 * @returns
	 */
	getFormationsByBeneficiaireId(
		beneficiaireId: number
	): Observable<MissionRequest[]> {
		if (beneficiaireId !== null && !isNaN(beneficiaireId)) {
			return this.genericCrudService.genericGetAPIData(
				`${this.appConstants.formationUrl}s/beneficiaire/${beneficiaireId}`,
				'getFormationsByBeneficiaireId'
			);
		}
		// Throw une erreur
		return null;
	}

	/**
	 * Récupère une missionRequest en fonction de son id
	 * @param id id de la missionRequest
	 * @returns
	 */
	getFormationById(id: number): Observable<MissionRequest> {
		return this.genericCrudService.genericGetAPIData(
			`${this.appConstants.formationUrl}/${id}`,
			'getFormationById'
		);
	}

	/**
	 * Récupère une mission en fonction de l'id de la missionRequest qui lui est associé
	 * @param id
	 * @returns
	 */
	getMissionByFormationId(id: number): Observable<Mission> {
		return this.genericCrudService.genericGetAPIData(
			`${this.appConstants.formationUrl}/${id}/mission`,
			'getMissionByFormationId'
		);
	}

	/**
	 * Met à jour une missionRequest
	 * @param formation missionRequest
	 * @returns
	 */
	update(formation: MissionRequest): Observable<MissionRequest> {
		return this.genericCrudService.genericPutDataToAPI(
			`${this.appConstants.formationUrl}`,
			'updateMissionRequest',
			formation
		);
	}

	/**
	 * Met à jour une missionRequest + nextMissions du group
	 * @param formation missionRequest
	 * @returns
	 */
	updatePartialGroup(formation: MissionRequest): Observable<MissionRequest> {
		return this.genericCrudService.genericPutDataToAPI(
			`${this.appConstants.formationUrl}/group/remaining`,
			'updatePartialGroup',
			formation
		);
	}

	/**
	 * Met à jour une missionRequest + all missions of group
	 * @param formation missionRequest
	 * @returns
	 */
	updateAllGroup(formation: MissionRequest): Observable<MissionRequest> {
		return this.genericCrudService.genericPutDataToAPI(
			`${this.appConstants.formationUrl}/group/all`,
			'updateAllGroup',
			formation
		);
	}

	/**
	 * Sauvegarde une missionRequest
	 * @param formation missionRequest
	 * @returns
	 */
	postFormation(formation: MissionRequest): Observable<void> {
		return this.genericCrudService.genericPostDataToAPI(
			`${this.appConstants.formationUrl}`,
			'postFormation',
			formation
		);
	}

	/**
	 * Supprime une missionRequest
	 * @param missionRequest
	 * @returns
	 */
	deletePartialGroup(missionRequest: MissionRequest): Observable<void> {
		return this.genericCrudService.genericDeleteAPIDataByID(
			`${this.appConstants.formationUrl}/${missionRequest.id}/group/remaining`,
			`deletePartialGroup`
		);
	}

	/**
	 * Supprime une missionRequest
	 * @param missionRequest
	 * @returns
	 */
	deleteFullGroup(missionRequest: MissionRequest): Observable<void> {
		return this.genericCrudService.genericDeleteAPIDataByID(
			`${this.appConstants.formationUrl}/${missionRequest.id}/group/all`,
			`deleteFullGroup`
		);
	}

	/**
	 * Supprime une missionRequest
	 * @param missionRequest
	 * @returns
	 */
	deleteFormation(missionRequest: MissionRequest): Observable<void> {
		return this.genericCrudService.genericDeleteAPIDataByID(
			`${this.appConstants.formationUrl}/${missionRequest.id}`,
			`deleteFormation`
		);
	}

	/**
	 * Supprime une liste de missionRequest
	 * @param missionRequests
	 * @returns
	 */
	deleteFormations(missionRequests: MissionRequest[]): Observable<void> {
		const httpOptions = {
			headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
			body: missionRequests.map((e) => e.id)
		};
		return this.genericCrudService.genericDeleteAPIDataByID(
			`${this.appConstants.formationUrl}`,
			`deleteFormation`,
			httpOptions
		);
	}

	/**
	 * Récupère la liste de missionRequest dont la date ce trouve dans la semaine de l'année donner
	 * @param year année
	 * @param numWeek semaine (entre 1 et 53 généralement)
	 * @returns
	 */
	getFormationsByDate(
		year: number,
		numWeek: number
	): Observable<MissionRequest[]> {
		return this.genericCrudService.genericGetAPIData(
			`${this.appConstants.formationUrl}s/${year}/${numWeek}`,
			'getFormationsByDate'
		);
	}

	importFormations(file: File): Observable<ImportResponse> {
		const formData: FormData = new FormData();
		formData.append('file', file, 'formations.csv');
		return this.genericCrudService.genericPostDataToAPI(
			`${this.appConstants.formationUrl}/import`,
			`importFormations`,
			formData
		);
	}

	desaffecter(missionRequestId: number): Observable<any> {
		return this.genericCrudService.genericPatchDataToAPI(
			`${this.appConstants.formationUrl}/${missionRequestId}/unassign`,
			`desaffecter`,
			null
		);
	}

	desaffecterCurrentAndNext(missionRequestId: number): Observable<any> {
		return this.genericCrudService.genericPatchDataToAPI(
			`${this.appConstants.formationUrl}/${missionRequestId}/unassign/next`,
			`desaffecterCurrentAndNext`,
			null
		);
	}

	desaffecterAll(missionRequestId: number): Observable<any> {
		return this.genericCrudService.genericPatchDataToAPI(
			`${this.appConstants.formationUrl}/${missionRequestId}/unassign/all`,
			`desaffecterAll`,
			null
		);
	}

	rescheduleMissionAndMissionRequest(dtoMission: Mission): Observable<void> {
		return this.genericCrudService.genericPatchDataToAPI(
			`${this.appConstants.formationUrl}/reschedule`,
			`rescheduleMissionAndMissionRequest`,
			dtoMission
		);
	}

	updateNotes(missionRequestId: number, notes: String): Observable<void> {
		return this.genericCrudService.genericPatchDataToAPI(
			`${this.appConstants.formationUrl}/${missionRequestId}/notes`,
			`updateNotes`,
			notes
		);
	}

	updateNombreIntervenant(missionRequestId: number): Observable<void> {
		return this.genericCrudService.genericPatchDataToAPI(
			`${this.appConstants.formationUrl}/${missionRequestId}/intervenant`,
			`updateNombreIntervenant`
		);
	}
}
